import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import type { FileEntry } from '@/lib/api/generated/model';
import { collectDroppedFiles } from '../droppedFiles';
import { useUploadWorkspaceFile } from './useUploadWorkspaceFile';

type PendingUpload = { targetPath: string; file: File };

/** One file's journey, from the moment its request goes out. */
export interface UploadItem {
  id: string;
  name: string;
  targetPath: string;
  file: File;
  status: 'uploading' | 'done' | 'failed';
}

export type FileUploads = ReturnType<typeof useFileUploads>;

/**
 * Everything uploading into `currentPath` needs: the two pickers, the drop
 * target, and the overwrite queue.
 *
 * A hook rather than the wrapper component this used to be, because the two
 * halves of uploading now live in different parts of the page — the menu that
 * starts an upload sits in the page header, while the drop target is still the
 * file-list pane (research.md R6). A component that owned both would have to
 * wrap the whole page to reach the header.
 *
 * Both individual files and whole folders are supported, from either source:
 * the folder *picker* uses a `webkitdirectory` input (whose files carry
 * `webkitRelativePath`), and a *dropped* folder is walked by
 * `collectDroppedFiles`. Either path produces the same `{ targetPath, file }`
 * shape, so they share one collision-check-and-upload pipeline.
 *
 * Collision checking (FR-009) only looks at `entries` — the current folder's
 * direct children — since that's the only listing already loaded. A file
 * landing directly in the current folder is checked; a file nested inside an
 * uploaded subfolder can't collide with anything already visible and uploads
 * without a confirmation prompt.
 */
export function useFileUploads(
  workspaceId: string,
  currentPath: string,
  entries: FileEntry[],
) {
  const uploadMutation = useUploadWorkspaceFile(workspaceId, currentPath);
  const filePickerRef = useRef<HTMLInputElement>(null);
  const folderPickerRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [collisionQueue, setCollisionQueue] = useState<PendingUpload[]>([]);
  const [items, setItems] = useState<UploadItem[]>([]);
  // Mirrors `items` for the handlers that have to read it rather than render
  // it. A retry fired twice before React re-renders would otherwise work from
  // the first render's snapshot and send a file that is already going again.
  const itemsRef = useRef<UploadItem[]>([]);
  // A dropped folder is walked in full before the first request goes out, and
  // on a large one that is a long stretch with nothing to show yet.
  const [isCollecting, setIsCollecting] = useState(false);
  const nextIdRef = useRef(0);
  const activeCollision = collisionQueue[0];

  function targetPathFor(relativePath: string): string {
    return currentPath ? `${currentPath}/${relativePath}` : relativePath;
  }

  function updateItems(
    update: (current: UploadItem[]) => UploadItem[],
  ): UploadItem[] {
    const next = update(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
    return next;
  }

  function setStatus(id: string, status: UploadItem['status']) {
    updateItems((current) =>
      current.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  }

  function send(id: string, targetPath: string, file: File) {
    // `mutateAsync` (not `mutate`) so each file's outcome is handled off its
    // own promise rather than `mutate`'s per-call `onError`/`onSuccess`
    // options — those are stored on the mutation's single shared observer, so
    // when a batch fires several `mutate()` calls back-to-back before any of
    // them settle, only the most recently registered callback actually runs;
    // earlier files in the same batch silently lose their outcome (FR-010).
    uploadMutation.mutateAsync({ path: targetPath, file }).then(
      () => {
        setStatus(id, 'done');
      },
      () => {
        setStatus(id, 'failed');
      },
    );
  }

  function startUpload(item: PendingUpload) {
    nextIdRef.current += 1;
    const id = `upload-${String(nextIdRef.current)}`;
    updateItems((current) => [
      ...current,
      {
        id,
        name: item.file.name,
        targetPath: item.targetPath,
        file: item.file,
        status: 'uploading',
      },
    ]);
    send(id, item.targetPath, item.file);
  }

  function enqueueUploads(items: PendingUpload[]) {
    const existingFilePaths = new Set(
      entries
        .filter((entry) => entry.kind === 'file')
        .map((entry) => entry.path),
    );
    const collisions: PendingUpload[] = [];
    for (const item of items) {
      if (existingFilePaths.has(item.targetPath)) {
        collisions.push(item);
      } else {
        startUpload(item);
      }
    }
    if (collisions.length > 0) {
      setCollisionQueue((queue) => [...queue, ...collisions]);
    }
  }

  // Ref callbacks rather than the ref objects themselves: everything here is
  // read during another component's render, and a ref that travels through a
  // return value is a ref read during render however it is spelled.
  return {
    isDraggingOver,
    activeCollision,
    isCollecting,
    items,

    /** Sends every file that failed once more, from the top. */
    retryFailed: () => {
      // The list to resend and the list marked as going again are read from
      // the same place, so a second retry before the first has rendered sees
      // the files it already restarted as already going.
      const retrying = itemsRef.current.filter(
        (item) => item.status === 'failed',
      );
      updateItems((current) =>
        current.map((item) =>
          item.status === 'failed' ? { ...item, status: 'uploading' } : item,
        ),
      );
      for (const item of retrying) {
        send(item.id, item.targetPath, item.file);
      }
    },

    dismissUploads: () => {
      updateItems(() => []);
    },

    setFilePicker: (element: HTMLInputElement | null) => {
      filePickerRef.current = element;
    },
    setFolderPicker: (element: HTMLInputElement | null) => {
      folderPickerRef.current = element;
      // `webkitdirectory` is not a React prop, so it is set on the element.
      if (element) element.webkitdirectory = true;
    },

    pickFiles: () => filePickerRef.current?.click(),
    pickFolder: () => folderPickerRef.current?.click(),

    onPickerChange: (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        enqueueUploads(
          Array.from(files).map((file) => ({
            targetPath: targetPathFor(file.webkitRelativePath || file.name),
            file,
          })),
        );
      }
      event.target.value = '';
    },

    dropTargetProps: {
      onDragEnter: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        dragCounterRef.current += 1;
        setIsDraggingOver(true);
      },
      onDragOver: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
      },
      onDragLeave: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDraggingOver(false);
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        dragCounterRef.current = 0;
        setIsDraggingOver(false);
        setIsCollecting(true);
        void collectDroppedFiles(event.dataTransfer)
          .then((dropped) => {
            enqueueUploads(
              dropped.map(({ path, file }) => ({
                targetPath: targetPathFor(path),
                file,
              })),
            );
          })
          .finally(() => {
            setIsCollecting(false);
          });
      },
    },

    confirmOverwrite: () => {
      if (!activeCollision) return;
      startUpload(activeCollision);
      setCollisionQueue((queue) => queue.slice(1));
    },

    cancelOverwrite: () => {
      setCollisionQueue((queue) => queue.slice(1));
    },
  };
}
